/**
 * Server-Sent Events vocabulary for the v2 conversational stack.
 *
 * A CLOSED, discriminated union of typed events streamed to the client (and to
 * operators) — NOT raw text streaming. Only the deterministic layer emits these
 * (never the LLM). They ride best-effort on a dedicated progress lane; the
 * authoritative turn outcome is always the terminal `AgentsReply`, so losing a
 * progress event is tolerable.
 *
 * Events are wrapped in `SseEventEnvelope`, which carries a per-conversation
 * sequential `id`. The conversation service is the single sequencer and SSE
 * fan-out owner; the client resumes via `Last-Event-ID` against a replay buffer.
 *
 * `text_delta` is intentionally absent — character-level text streaming is out
 * of scope; the vocabulary is discrete per turn.
 */

import { z } from 'zod';

import { ConversationIdSchema } from './message_envelope';

export const SseEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('turn_start') }),
  z.object({
    type: z.literal('gate_status'),
    gate: z.enum(['channel', 'policy']),
    status: z.enum(['pass', 'block']),
    reason: z.string().optional(),
  }),
  z.object({ type: z.literal('tool_pending'), tool: z.string(), label: z.string().optional() }),
  z.object({ type: z.literal('tool_done'), tool: z.string(), ok: z.boolean() }),
  z.object({
    type: z.literal('ui_component'),
    slotId: z.string(),
    component: z.string(),
    data: z.unknown(),
  }),
  z.object({ type: z.literal('ui_patch'), slotId: z.string(), patch: z.unknown() }),
  z.object({
    type: z.literal('turn_end'),
    state: z.enum(['awaiting_user', 'awaiting_approval', 'executing', 'done']),
    text: z.string().optional(),
  }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
  z.object({ type: z.literal('ping') }),
]);

export type SseEvent = z.infer<typeof SseEventSchema>;
export type SseEventType = SseEvent['type'];

/**
 * Transport envelope: one SSE event plus routing + ordering metadata. `id` is
 * monotonic per conversation, assigned by the conversation service at fan-out
 * (one sequencer per conversation) → drives `Last-Event-ID`/replay. `.strict()`
 * at the top level surfaces unexpected keys as parse errors at every consumer.
 */
export const SseEventEnvelopeSchema = z
  .object({
    conversationId: ConversationIdSchema,
    id: z.number().int().nonnegative(),
    event: SseEventSchema,
  })
  .strict();

export type SseEventEnvelope = z.infer<typeof SseEventEnvelopeSchema>;
